import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { SearchTagsDateHeaderFilters } from '@/features/shared/components/SearchTagsDateHeaderFilters';
import { s, typographyTokens } from '@/theme/tokens';
import { uiTokens, type UiTone } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites/charts/ChartEmptyState';
import { Panel } from '@/ui/composites/Panel';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import {
  BarChart,
  Box,
  Button,
  LineChart,
  LineChartSkeleton,
  StackedBarChartSkeleton,
  Text,
} from '@/ui/primitives';
import { StackedBarChart, type StackedBarChartDatum } from '@/ui/primitives/StackedBarChart';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { DashboardRange } from '@/features/dashboard/hooks';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ReactElement, ReactNode } from 'react';

export type EntityChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

export type EntityStackedBarSeries = {
  key: string;
  label: string;
  color?: string;
  colorKey?: UiTone;
};

type EntityPageContentProps<TActiveTab extends string> = {
  range: DashboardRange;
  chartTab: EntityChartTab;
  activeTab: TActiveTab;
  entityTabValue: TActiveTab;
  searchValue: string;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  hasCustomDates: boolean;
  isLatestMode: boolean;
  metricCards: MetricCardItem[];
  selectedStatuses: string[];
  statusOptions?: Array<{ value: string; label: string }>;
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  metricValueItems: MetricValueItem[];
  chartHeight: number;
  showChartSkeleton: boolean;
  suppressChartAnimation: boolean;
  showLineChartPoints: boolean;
  filteredStackedChartData: {
    data: StackedBarChartDatum[];
    series: EntityStackedBarSeries[];
  } | null;
  filteredPassRateChartData: Array<Record<string, string | number>> | null;
  filteredFrequencyChartData: Array<Record<string, string | number>> | null;
  filteredDurationChartData: Array<Record<string, string | number>> | null;
  passRateGuideLines: Array<{ value: number; color: string }>;
  frequencyGuideLines: Array<{ value: number; color: string }>;
  durationGuideLines: Array<{ value: number; color: string }>;
  deltasEnabled: boolean;
  onRangeChange: (nextRange: DashboardRange) => void;
  onSearchChange: (value: string) => void;
  onChartTabChange: (nextTab: EntityChartTab) => void;
  onActiveTabChange: (nextTab: TActiveTab) => void;
  onStatusToggle: (status: string) => void;
  onStatusesChange?: (nextStatuses: string[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onChartSelect: (dataIndex: number, seriesKey?: string) => void;
  onSelectedStartDayChange: (nextDay: string | null) => void;
  onSelectedEndDayChange: (nextDay: string | null) => void;
  showChartDateClear: boolean;
  onChartDateClear: () => void;
  titleActions?: ReactNode;
  footer?: ReactNode;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  renderEntityTable: (showTableDeltas: boolean, noRowsText?: string) => ReactElement;
  renderExecutionsTable: (noRowsText?: string) => ReactElement;
  chartEmptyStateTitle?: string;
  entityTableNoRowsText?: string;
  executionTableNoRowsText?: string;
  contentBelow?: ReactNode;
  contentOverride?: ReactNode;
};

const hasVisibleChartValue = (
  data: Array<Record<string, string | number>> | null,
  key: string,
): boolean =>
  Boolean(
    data?.some((row) => {
      const value = row[key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

const hasVisibleStackedChartValue = (
  data: Array<Record<string, string | number>>,
  series: EntityStackedBarSeries[],
): boolean =>
  data.some((row) =>
    series.some((seriesItem) => {
      const value = row[seriesItem.key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

export const EntityPageContent = <TActiveTab extends string>({
  range,
  chartTab,
  activeTab,
  entityTabValue,
  searchValue,
  pickerStartDay,
  pickerEndDay,
  hasCustomDates,
  isLatestMode,
  metricCards,
  selectedStatuses,
  statusOptions,
  selectedTags,
  tagOptions,
  tagOptionsLoading,
  metricValueItems,
  chartHeight,
  showChartSkeleton,
  suppressChartAnimation,
  showLineChartPoints,
  filteredStackedChartData,
  filteredPassRateChartData,
  filteredFrequencyChartData,
  filteredDurationChartData,
  passRateGuideLines,
  frequencyGuideLines,
  durationGuideLines,
  deltasEnabled,
  onRangeChange,
  onSearchChange,
  onChartTabChange,
  onActiveTabChange,
  onStatusToggle,
  onStatusesChange,
  onTagsChange,
  onChartSelect,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  showChartDateClear,
  onChartDateClear,
  titleActions,
  footer,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  renderEntityTable,
  renderExecutionsTable,
  chartEmptyStateTitle = 'No data found',
  entityTableNoRowsText,
  executionTableNoRowsText,
  contentBelow,
  contentOverride,
}: EntityPageContentProps<TActiveTab>): ReactElement => {
  const tabsContentGap = 'md' as const;
  const isDeltaToggleDisabled = isLatestMode || range === 'all';
  const showTableDeltas = deltasEnabled && !isDeltaToggleDisabled;
  const hasStatusData = filteredStackedChartData
    ? hasVisibleStackedChartValue(filteredStackedChartData.data, filteredStackedChartData.series)
    : false;
  const hasPassRateData = hasVisibleChartValue(filteredPassRateChartData, 'passRate');
  const hasFrequencyData = hasVisibleChartValue(filteredFrequencyChartData, 'frequency');
  const hasDurationData = hasVisibleChartValue(filteredDurationChartData, 'duration');
  const summary = (
    <Stack gap="lg">
      <StatusMetricCardsRow
        cards={metricCards}
        selectedIds={selectedStatuses}
        onToggle={(status) => {
          if (status === 'total') return;
          onStatusToggle(status);
        }}
      />
      <MetricValueCardsRow items={metricValueItems} />
    </Stack>
  );
  const charts = (
    <Panel padding="md" gap="none" contentGap="lg" sx={{ backgroundColor: 'background.paper' }}>
      <Stack gap={tabsContentGap}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" justify="space-between" sx={{ minWidth: 0 }}>
            <Tabs
              value={chartTab}
              onChange={(_, next) => {
                onChartTabChange(next as EntityChartTab);
              }}
              aria-label={chartTabsAriaLabel}
            >
              <Tab label="Status" value="status" />
              <Tab label="Pass Rate" value="pass-rate" />
              <Tab label="Frequency" value="frequency" />
              <Tab label="Duration" value="duration" />
            </Tabs>
            {showChartDateClear ? (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  onChartDateClear();
                }}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontSize: typographyTokens.captionSmall.fontSize,
                  mr: s('md'),
                  background: 'none',
                  border: 'none',
                  padding: s('none'),
                  userSelect: 'none',
                  font: 'inherit',
                  lineHeight: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                RESET
              </Box>
            ) : null}
          </Stack>
        </Box>
        {showChartSkeleton ? (
          chartTab === 'frequency' || chartTab === 'duration' ? (
            <LineChartSkeleton height={chartHeight} />
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartTab === 'status' ? (
          filteredStackedChartData ? (
            hasStatusData ? (
              <StackedBarChart
                data={filteredStackedChartData.data}
                series={filteredStackedChartData.series}
                height={chartHeight}
                skipAnimation={suppressChartAnimation}
                onSelectBar={onChartSelect}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body="Try adjusting filters or search."
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : chartTab === 'pass-rate' ? (
          filteredPassRateChartData ? (
            hasPassRateData ? (
              <BarChart
                data={filteredPassRateChartData}
                series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={passRateGuideLines}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body="Try adjusting filters or search."
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : chartTab === 'frequency' ? (
          filteredFrequencyChartData ? (
            hasFrequencyData ? (
              <LineChart
                data={filteredFrequencyChartData}
                series={[{ key: 'frequency', label: '', color: 'var(--color-pink)' }]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={frequencyGuideLines}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
                showPoints={showLineChartPoints || filteredFrequencyChartData.length === 1}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body="Try adjusting filters or search."
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : filteredDurationChartData ? (
          hasDurationData ? (
            <LineChart
              data={filteredDurationChartData}
              series={[{ key: 'duration', label: '' }]}
              height={chartHeight}
              showLegend={false}
              yReferenceLines={durationGuideLines}
              onSelectBar={(dataIndex) => {
                onChartSelect(dataIndex);
              }}
              showPoints={showLineChartPoints || filteredDurationChartData.length === 1}
            />
          ) : (
            <Box
              sx={{
                height: chartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState
                title={chartEmptyStateTitle}
                body="Try adjusting filters or search."
              />
            </Box>
          )
        ) : (
          <Box sx={{ height: chartHeight }} />
        )}
      </Stack>
    </Panel>
  );
  const content = contentOverride ?? (
    <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
      <Stack gap={tabsContentGap}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" sx={{ minWidth: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(_, next) => {
                onActiveTabChange(next as TActiveTab);
              }}
              aria-label={pageTabsAriaLabel}
            >
              <Tab label={entityTabLabel} value={entityTabValue} />
              <Tab label="Executions" value="executions" />
            </Tabs>
          </Stack>
        </Box>
        {activeTab === entityTabValue
          ? renderEntityTable(showTableDeltas, entityTableNoRowsText)
          : renderExecutionsTable(executionTableNoRowsText)}
      </Stack>
    </Panel>
  );
  const resolvedContent = contentBelow ? (
    <Stack gap="md">
      {content}
      {contentBelow}
    </Stack>
  ) : (
    content
  );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="sectionTitle" component="h1">
          {title}
        </Text>
      }
      titleActions={titleActions ?? <Button>{addButtonLabel}</Button>}
      headerActions={
        <SearchTagsDateHeaderFilters
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchAriaLabel={searchAriaLabel}
          selectedStatuses={selectedStatuses}
          onStatusesChange={onStatusesChange}
          statusOptions={statusOptions}
          selectedTags={selectedTags}
          onTagsChange={onTagsChange}
          tagOptions={tagOptions}
          tagOptionsLoading={tagOptionsLoading}
          range={range}
          onRangeChange={onRangeChange}
          pickerStartDay={pickerStartDay}
          pickerEndDay={pickerEndDay}
          hasCustomDates={hasCustomDates}
          isLatestMode={isLatestMode}
          onSelectedStartDayChange={onSelectedStartDayChange}
          onSelectedEndDayChange={onSelectedEndDayChange}
          includeLatest
        />
      }
      summary={summary}
      charts={charts}
      content={resolvedContent}
      footer={footer}
    />
  );
};
